// Lightweight PostHog wrapper. Safe to call before init (no-ops).
//
// PostHog is dynamically imported so the ~50 kB analytics SDK is NOT in the
// initial entry chunk that the public landing page downloads. It loads in
// parallel after first paint when initAnalytics() is called.
//
// Key is a publishable PostHog project key — fine to ship in client code.
type PostHogModule = typeof import("posthog-js").default;

// Prefer the linked PostHog connector key; fall back to the baked-in key.
const POSTHOG_KEY =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY as string | undefined) ??
  "phc_mApGYDCqoYqjNBfN7kWu2eQuuREBLGGZnZYCpk3Kk9ZH";
const POSTHOG_HOST =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION as string | undefined) === "eu"
    ? "https://eu.i.posthog.com"
    : "https://us.i.posthog.com";

let initialised = false;
let posthog: PostHogModule | null = null;
// Buffer for early calls so events fired before the dynamic import resolves
// aren't lost.
const pending: Array<(ph: PostHogModule) => void> = [];

function withPosthog(fn: (ph: PostHogModule) => void) {
  if (posthog) return fn(posthog);
  if (initialised) pending.push(fn);
}

export function initAnalytics() {
  if (initialised) return;
  if (typeof window === "undefined") return;
  initialised = true;
  // Dynamic import keeps posthog-js out of the entry bundle.
  import("posthog-js")
    .then((mod) => {
      const ph = mod.default;
      ph.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: "identified_only",
        // We trigger pageviews manually so SPA navigation is tracked, not
        // just the initial load.
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
        mask_all_text: false,
        session_recording: { maskAllInputs: true },
      });
      posthog = ph;
      // Drain anything queued while the SDK was loading.
      const queued = pending.splice(0, pending.length);
      queued.forEach((cb) => {
        try {
          cb(ph);
        } catch (e) {
          console.warn("[analytics] queued call failed", e);
        }
      });
    })
    .catch((e) => {
      console.warn("[analytics] failed to load posthog", e);
    });
}

/** Identify the current user once authenticated. Safe to call repeatedly. */
export function identifyUser(userId: string, traits: Record<string, unknown> = {}) {
  withPosthog((ph) => ph.identify(userId, traits));
}

/** Drop identity on logout. */
export function resetAnalytics() {
  withPosthog((ph) => ph.reset());
}

/** Fire a pageview for the given path. */
export function trackPageView(path: string) {
  withPosthog((ph) =>
    ph.capture("$pageview", { $current_url: window.location.origin + path }),
  );
}

/** Generic event tracker. Never throws. */
export function track(event: string, properties: Record<string, unknown> = {}) {
  withPosthog((ph) => {
    try {
      ph.capture(event, properties);
    } catch (e) {
      console.warn("[analytics] capture failed", event, e);
    }
  });
}

// Named events used across the app — keep this list as the single source of truth.
export const events = {
  signupStarted: "signup_started",
  signupCompleted: "signup_completed",
  loginCompleted: "login_completed",
  subscriptionCheckoutStarted: "subscription_checkout_started",
  subscriptionStarted: "subscription_started",
  aiFeatureUsed: "ai_feature_used",
} as const;
